import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';

export function useStoreSettingsApi() {
  const { get, post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: false,
    },
  }));

  const listReviewsProviders = async (): Promise<ReviewProviders> =>
    (await get(routes.reviewsEndpoint())) as ReviewProviders;

  const setReviewsProvider = async (
    providerName: ReviewProviderName,
  ): Promise<void> => post(routes.reviewsEndpoint(), { value: providerName });

  const getProductExcludedTags = async (): Promise<ProductExcludedTags> =>
    (await get(routes.getProductExcludedTags())) as ProductExcludedTags;

  const setProductExcludedTags = async (
    tags: string[],
  ): Promise<ProductExcludedTags> =>
    post(routes.upsertProductExcludedTags(), { tags });

  const setIsShopRTL = async (isRtlValue: boolean): Promise<void> =>
    post(routes.setShopRTL(), { isRtlValue });

  const isShopRTL = async (): Promise<IsRtlValue> =>
    (await get(routes.isShopRTL)) as IsRtlValue;

  const getVariantMetafields = async (): Promise<VariantMetafieldsResp> =>
    (await get(routes.getStoreVariantMetafields())) as VariantMetafieldsResp;

  const getUserSettings = () =>
    get(routes.userSettings) as Promise<UserSettings>;
  const setUserSettings = (settings: {
    isConfidenceIntervalsEnabled: 'YES' | 'NO';
  }) => post(routes.userSettings, settings) as Promise<UserSettings>;

  const setVariantMetafields = async (
    metafields: VariantMetafield[],
  ): Promise<VariantMetafieldsResp> =>
    post(routes.setStoreVariantMetafields(), { metafields });

  return {
    setIsShopRTL,
    isShopRTL,
    listReviewsProviders,
    setReviewsProvider,
    loading,
    getProductExcludedTags,
    setProductExcludedTags,
    getVariantMetafields,
    setVariantMetafields,
    getUserSettings,
    setUserSettings,
  };
}

export interface ReviewsData {
  reviewsProviders: ReviewProviders;
  reviewsEnabled: boolean;
  hasReviewProviders: boolean;
}

export interface ReviewProviders {
  providers?: ReviewProvider[];
}

export interface ReviewProvider {
  name?: ReviewProviderName;
  canBeSelected?: boolean;
  isSelected?: boolean;
}

export interface ProductExcludedTags {
  tags: string[];
}

export interface VariantMetafield {
  namespace?: string;
  keys?: string[];
}

export interface VariantMetafieldsResp {
  metafields: VariantMetafield[];
}

export interface IsRtlValue {
  isRtlValue: boolean;
}

export enum ReviewProviderName {
  NA = `____NA`,
  LOOX = `LOOX`,
  OKENDO = `OKENDO`,
  SHOPIFY = `SHOPIFY`,
  KLAVIYO = `KLAVIYO`,
  STAMPED = `STAMPED`,
  YOTPO = `YOTPO`,
}

export type UserSettings = {
  isConfidenceIntervalsEnabled: 'YES' | 'NO';
};
